/**
 * user-context.js
 */

import React from "react";
import axios from 'axios';
import { useHistory } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { UserDataProvider } from "contexts/user-data/user-data-context";
import useRecordStore from "contexts/record-store/record-store";


const defaultState = {
  user: null,
  admin: null,
};


export const UserContext = React.createContext({
  ...defaultState,
  setUser: (user) => {},
  setRole: (role) => {},
  setAdmin: (admin) => {},
  impersonateUser: (userId) => {},
});


export const UserProvider = ({ children }) => {
  const recordStore = useRecordStore();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = React.useState(defaultState);
  const [userData, setUserData] = React.useState();


  // set user
  const setUser = React.useCallback((user) => {
    if (user) {
      const { data, ...newUser } = user;
      setState(p => ({
        ...p,
        user: newUser
      }));
      setUserData(data);
    } else {
      // clear user and userdata
      setState(defaultState);
      setUserData(null);
    }
  }, []);


  // sets user role and resets app state
  const setRole = React.useCallback(async (role) => {
    recordStore.reset();

    const response = await axios.post('/account/role', { id: role.id });

    if (response.status === 200) {
      if (response.data.username) {
        enqueueSnackbar(`Role changed to ${role.name}`, { variant: 'info' });
        history.push('/');
        setUser(response.data);
      } else if (response.data.redirect) {
        window.location.href = response.data.url;
      }
    }
  // recordStore is complex object and can't be a dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser, enqueueSnackbar, history]);


  // set admin
  const setAdmin = React.useCallback((admin) => {
    setState(p => ({
      ...p,
      admin
    }));
  }, []);


  // impersonate user
  const impersonateUser = React.useCallback(async (userId) => {
    try {
      const response = await axios.post(`/account/impersonate/${userId}`);
      if (response.isSuccess) {
        setUser(response.data);
        return true;
      }
    } catch {}
    return false;
  }, [setUser]);


  // final state value
  const value = React.useMemo(() => ({
    ...state,
    setUser,
    setRole,
    setAdmin,
    impersonateUser,
  }), [
    state,
    setUser,
    setRole,
    setAdmin,
    impersonateUser,
  ]);


  return (
    <UserContext.Provider value={value}>
      <UserDataProvider data={userData}>
        {children}
      </UserDataProvider>
    </UserContext.Provider>
  );
};